"use client";

// =========================================================================
// Usage tracking in the browser — a provider and a separate page-view probe
// =========================================================================
//
// WHY THE BROWSER AND NOT THE MIDDLEWARE. `proxy.ts` already resolves the
// session and the role on every protected navigation and looks like the perfect
// seam, but Hub's middleware runs on the EDGE runtime, and a service-role
// Supabase write does not belong there — switching the whole app's middleware
// runtime to make analytics work would be a platform change hiding inside a
// feature.
//
// So capture is split by what each half can actually guarantee:
//
//   - SIGN-INS are server-side truth. GoTrue writes `auth.audit_log_entries`
//     whatever the browser does, and the rollup cron replays them. A blocked
//     script cannot hide that somebody signed in.
//   - PAGE VIEWS and in-app actions come from here. They can be blocked, and
//     that is an acceptable loss for the question they answer ("which parts do
//     they use") as long as the load-bearing question ("do they come at all")
//     does not depend on them.
//
// WHY TWO COMPONENTS. Under `cacheComponents`, a Client Component that reads
// URL data (`usePathname`) may not be prerendered outside a Suspense boundary —
// the build fails with CLIENT_HOOK_DYNAMIC. The provider has to sit high enough
// to serve the whole tree, so it reads no URL at all; the probe that does read
// the pathname is a leaf with its own boundary, exactly like the four shell
// pieces already in the root layout.
// =========================================================================

import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";

import { useUsageTracker } from "@west-tennessee-consulting/usage-analytics/react";

import { HUB_USAGE_CATALOG, type HubUsageEventName } from "@/lib/usage/catalog";
import { shouldRecordPath, surfaceForPath } from "@/lib/usage/surface";

const ENDPOINT = "/api/usage/events";

export interface UsageTrackingValue {
  track: (
    event: HubUsageEventName,
    detail?: { surface?: string; objectType?: string; objectId?: string },
  ) => void;
}

/**
 * Default is a no-op, so a component outside the provider (a boundary page, a
 * test render) still calls `track()` safely. Telemetry must never be the reason
 * a page fails to render.
 */
const UsageTrackingContext = createContext<UsageTrackingValue>({ track: () => {} });

export function useUsageTracking(): UsageTrackingValue {
  return useContext(UsageTrackingContext);
}

export function UsageTrackingProvider({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  const tracker = useUsageTracker({
    catalog: HUB_USAGE_CATALOG,
    endpoint: ENDPOINT,
    enabled,
  });

  const value = useMemo<UsageTrackingValue>(
    () => ({
      track: (event, detail) => {
        tracker.track(event, detail);
      },
    }),
    [tracker],
  );

  return <UsageTrackingContext.Provider value={value}>{children}</UsageTrackingContext.Provider>;
}

/**
 * Records one `page_view` per navigation. Mount inside a Suspense boundary.
 *
 * It sends the SURFACE, never the path: `/tickets/<uuid>` would put a record id
 * in an analytics table and make every dedupe key unique, which is the same as
 * having no dedupe.
 */
export function UsagePageViewTracker() {
  const pathname = usePathname();
  const { track } = useUsageTracking();

  useEffect(() => {
    if (!pathname || !shouldRecordPath(pathname)) return;
    track("page_view", { surface: surfaceForPath(pathname) });
  }, [pathname, track]);

  return null;
}
